import { ChangeEvent, useRef, useState } from 'react';
import styled from 'styled-components';

import { SupplierGroupMessage } from '../../../../../types/SupplierGroupMessage';
import { Button } from '../../../../cdl/Button/Button';
import { IconButton } from '../../../../cdl/IconButton/IconButton';
import { Text } from '../../../../cdl/Text/Text';
import { useToasts } from '../../../../cdl/Toast/useToasts';
import { translate } from '../../../../common/helpers/translate.helper';
import { useRole } from '../../../../common/hooks/useRole';
import { IconDownload } from '../../../../common/icons/cdl/Download';
import { IconTrash } from '../../../../common/icons/cdl/Trash';
import { IconUpload } from '../../../../common/icons/cdl/Upload';
import { Box } from '../../../../common/ui/Box';
import { theme } from '../../../../common/ui/theme';
import { useSupplierGroupLogoDelete } from '../hooks/useSupplierGroupLogoDelete';
import { useSupplierGroupLogoUpload } from '../hooks/useSupplierGroupLogoUpload';

interface SupplierGroupImageDisplayProps extends Pick<SupplierGroupMessage, 'logo' | 'id'> {}

const PlaceholderContainer = styled.div`
    width: 300px;
    height: 200px;
    border-width: 1px;
    border-style: solid;
    border-color: ${theme.colors.border.muted};
    background-color: ${theme.colors.background.inset};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    border-radius: 8px;
`;

const ImageContainer = styled.div`
    width: 300px;
    position: relative;
    overflow: hidden;
    transition: all 0.2s ease-in-out;
    padding: ${theme.space[4]};
`;

const ImageOverlay = styled.div<{ isVisible: boolean }>`
    position: absolute;
    top: 0px;
    left: 0px;
    right: 0px;
    bottom: 0px;
    background-color: ${theme.colors.background.inset};
    opacity: ${({ isVisible }) => (isVisible ? 0.75 : 0)};
    transition: opacity 0.2s ease-in-out;
    pointer-events: none;
`;

const ButtonOverlay = styled.div<{ isVisible: boolean }>`
    position: absolute;
    top: 8px;
    left: 8px;
    right: 8px;
    bottom: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
    transition: opacity 0.2s ease-in-out;
    pointer-events: ${({ isVisible }) => (isVisible ? 'auto' : 'none')};
`;

const PlaceholderContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${theme.space[16]};
    text-align: center;
`;

const HiddenInput = styled.input`
    position: absolute;
    width: 1px;
    height: 1px;
    display: none;
`;

export const SupplierGroupImageDisplay = ({ logo, id }: SupplierGroupImageDisplayProps) => {
    const { addLoadingToast } = useToasts();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isHovered, setIsHovered] = useState(false);
    const role = useRole();

    const uploadLogoMutation = useSupplierGroupLogoUpload(id);
    const deleteLogoMutation = useSupplierGroupLogoDelete(id);

    const handleUploadClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            addLoadingToast(
                () => uploadLogoMutation.mutateAsync(file),
                translate('suppliergroup.toasts.logoUploadLoading'),
                translate('suppliergroup.toasts.logoUploadSuccess'),
                translate('suppliergroup.toasts.logoUploadError')
            );
        }
        event.target.value = '';
        setIsHovered(false);
    };

    const handleDeleteClick = () => {
        addLoadingToast(
            () => deleteLogoMutation.mutateAsync(),
            translate('suppliergroup.toasts.deleteLogoLoading'),
            translate('suppliergroup.toasts.deleteLogoSuccess'),
            translate('suppliergroup.toasts.deleteLogoError')
        );
        setIsHovered(false);
    };

    if (!role.hasAdminRights() && !logo) {
        return null;
    }

    if (!logo) {
        return (
            <PlaceholderContainer onClick={handleUploadClick}>
                <PlaceholderContent>
                    <Button LeadingIcon={IconUpload}>{translate('suppliergroup.uploadImagePlaceholder')}</Button>
                    <Text variant="extraSmall" color="foreground.muted">
                        {translate('suppliergroup.logoUploadDescription')}
                    </Text>
                </PlaceholderContent>
                <HiddenInput ref={fileInputRef} type="file" accept="image/jpeg,image/png" onChange={handleFileChange} />
            </PlaceholderContainer>
        );
    }

    return (
        <ImageContainer onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
            <img width="100%" src={logo.url} alt={translate('suppliergroup.logoAlt')} />
            {role.hasAdminRights() ? (
                <>
                    <ImageOverlay isVisible={isHovered} />
                    <ButtonOverlay isVisible={isHovered}>
                        <Box display="flex" gap={4}>
                            <IconButton
                                Icon={IconUpload}
                                emphasis="medium"
                                tooltipLabel={translate('suppliergroup.btnUploadLogo')}
                                onClick={handleUploadClick}
                            />
                            <IconButton
                                as="a"
                                href={logo.url}
                                target="_blank"
                                download
                                emphasis="medium"
                                destructive={false}
                                Icon={IconDownload}
                                tooltipLabel={translate('suppliergroup.btnDownloadLogo')}
                            />
                            <IconButton
                                Icon={IconTrash}
                                emphasis="medium"
                                destructive
                                tooltipLabel={translate('suppliergroup.btnRemoveLogo')}
                                onClick={handleDeleteClick}
                            />
                        </Box>
                    </ButtonOverlay>
                </>
            ) : null}
            <HiddenInput ref={fileInputRef} type="file" accept="image/jpeg,image/png" onChange={handleFileChange} />
        </ImageContainer>
    );
};
